import { NumberUtil, UrlUtil } from '@hugendubel/utils';
import { Track, TRACK_ID } from '@hugendubel/tracking_client';
import { Toaster } from '@hugendubel/react-basic';
import { ProductFilter, WS } from '../ws/WS';
import { PageableList } from '@hugendubel/ws_client';
import { Store } from '../store';
import { Globals } from '../globals';
import { WsStockInfo } from '../ws/result/WsStockInfo';
import { Config } from '../config';
import { externalHistory } from '../ui/hooks/useExposeNavigate';
import { ReducedProduct } from '../ws/result/ReducedProduct';
import { ClientFetch } from '../client-fetch';

export enum SortField {
    BONUS_COST = 'bonus_cost',
    TITLE = 'title',
    AUTHORS = 'authors',
    PRICE = 'price',
    PUBLICATION_DATE = 'publication',
}

const SortFieldValues: SortField[] = Object.values(SortField);

const ProductFilterValues: ProductFilter[] = Object.values(ProductFilter);

export enum PointLimit {
    ALL = 'all',
    AFFORDABLE = 'affordable',
}

type Query = { [key: string]: string | undefined };

export type ParsedQuery = {
    page: number;
    showAll: boolean;
    sortField: SortField;
    ascending: boolean;
    productFilter?: ProductFilter;
    eanFilter?: string;
};

type _SearchServiceType = {
    getCurrentQuery: () => Query;
    getPageFromQuery: (query: Query) => number;
    getShowAllFromQuery: (query: Query) => boolean;
    getSortFromQuery: (query: Query) => SortField;
    getAscendingFromQuery: (query: Query) => boolean;
    getProductFilterFromQuery: (query: Query) => ProductFilter | undefined;
    getEanFilterFromQuery: (query: Query) => string | undefined;
    getParsedForQuery: (query: Query) => ParsedQuery;
    getParsedForSearch: (search: string) => ParsedQuery;
    getParsedQuery: () => ParsedQuery;
    loadStockInfo: ({ items }: PageableList<ReducedProduct>) => Promise<void>;
    firstQuery: boolean;
    needsReplacedSearch: (articleLength: number) => boolean;
};

export const _SearchService: _SearchServiceType = {} as any;
const DEFAULT_MAX_RESULTS = 12;
_SearchService.getCurrentQuery = (): Query => UrlUtil.locationSearchToQuery(Globals.window().location.search) as Query;

_SearchService.getPageFromQuery = (query: Query): number => NumberUtil.getPositiveInteger(query.page, 1);

const getPageFromSearch = (search: string): number =>
    _SearchService.getPageFromQuery(UrlUtil.locationSearchToQuery(search) as Query);

_SearchService.getShowAllFromQuery = (query: Query): boolean => query.show === PointLimit.ALL;

const getShowAllFromSearch = (search: string): boolean =>
    _SearchService.getShowAllFromQuery(UrlUtil.locationSearchToQuery(search) as Query);

_SearchService.getSortFromQuery = (query: Query): SortField =>
    SortFieldValues.find(v => v === query.sort) || SortField.BONUS_COST;

const getSortFromSearch = (search: string): SortField =>
    _SearchService.getSortFromQuery(UrlUtil.locationSearchToQuery(search) as Query);

_SearchService.getAscendingFromQuery = (query: Query): boolean => query.ascending === 'true';

const getAscendingFromSearch = (search: string): boolean =>
    _SearchService.getAscendingFromQuery(UrlUtil.locationSearchToQuery(search) as Query);

_SearchService.getProductFilterFromQuery = (query: Query): ProductFilter | undefined =>
    ProductFilterValues.find(v => v === query.pf);

const getProductFilterFromSearch = (search: string): ProductFilter | undefined =>
    _SearchService.getProductFilterFromQuery(UrlUtil.locationSearchToQuery(search) as Query);

_SearchService.getEanFilterFromQuery = (query: Query): string | undefined => query.ean;

_SearchService.getParsedForQuery = (query: Query): ParsedQuery => ({
    page: _SearchService.getPageFromQuery(query),
    showAll: _SearchService.getShowAllFromQuery(query),
    sortField: _SearchService.getSortFromQuery(query),
    ascending: _SearchService.getAscendingFromQuery(query),
    productFilter: _SearchService.getProductFilterFromQuery(query),
    eanFilter: _SearchService.getEanFilterFromQuery(query),
});

_SearchService.getParsedForSearch = (search: string): ParsedQuery =>
    _SearchService.getParsedForQuery(UrlUtil.locationSearchToQuery(search) as Query);

_SearchService.getParsedQuery = (): ParsedQuery => _SearchService.getParsedForQuery(_SearchService.getCurrentQuery());

export type SearchQuery = {
    // we use here the more strict type number,
    // because for writing into the search string that makes no difference, e.g. "2" and 2 are producing both "page=2"
    page?: number;
    show?: PointLimit;
    sort?: SortField;
    // we use here the more strict type boolean,
    // because for writing into the search string that makes no difference, e.g. "true" and true are producing both "ascending=true"
    ascending?: boolean;
    pf?: ProductFilter;
    ean?: string;
};

const calculateUrl = (updates: SearchQuery): string => UrlUtil.updateSearch(updates);

_SearchService.loadStockInfo = ({ items }: PageableList<ReducedProduct>): Promise<void> => {
    const eanList = items
        .map(el => el.attributes?.ean)
        .filter(Boolean)
        .join(',');

    if (!eanList) return Promise.resolve(); // nothing to do

    return WS.loadStockInfo({ eanList })
        .then(({ eanAvailabilityMap }: WsStockInfo) => {
            Store.set(state => ({ eanAvailabilityMap: { ...state.eanAvailabilityMap, ...eanAvailabilityMap } }));
        })
        .catch((error: Error) => {
            Track.error(TRACK_ID('2018-09-10 11:24:17'), 'Could not load stock info, because: ' + error.message);
        });
};

_SearchService.firstQuery = true;

_SearchService.needsReplacedSearch = articleLength => {
    if (_SearchService.firstQuery) {
        _SearchService.firstQuery = false;
        if (!articleLength) {
            externalHistory.navigate(
                calculateUrl({
                    sort: SortField.PUBLICATION_DATE,
                    ascending: false,
                    page: 1,
                    show: PointLimit.ALL,
                }),
                { replace: true }
            );
            return true;
        }
    }
    return false;
};

const loadArticles = (points: number): Promise<void> => {
    const { page, showAll, sortField, ascending, productFilter, eanFilter } = _SearchService.getParsedQuery();
    const {
        customerData: { pseudoCustomer },
        isPhysicalBookstore,
    } = Config.get();

    const params = {
        maxPoints: showAll ? undefined : points,
        offset: (page - 1) * DEFAULT_MAX_RESULTS,
        maxResults: DEFAULT_MAX_RESULTS,
        eanFilter,
        sortField,
        ascending,
        productFilter: pseudoCustomer ? ProductFilter.NO_DOWNLOADS : productFilter,
    };

    return ClientFetch.bonusShopSearch(params)
        .then((res: Response) => res.json())
        .then((articles: PageableList<ReducedProduct>) => {
            if (!_SearchService.needsReplacedSearch(articles.items.length)) {
                // make sure to set the articles only when we would not replace the search params
                // else the customer would get displayed for a short time, that there were no results
                Store.set({ articles });
                if (isPhysicalBookstore) {
                    // do NOT wait for the stock info (this call is very slow and we do not want to wait so long)
                    _SearchService.loadStockInfo(articles);
                }
            }
        })
        .catch((error: Error) => {
            Track.error(TRACK_ID('2018-09-10 10:35:02'), 'Could not load articles, because: ' + error.message);
            Toaster.open({ id: 'error.message.general' });
        });
};

const getSearchId = (search: string) => UrlUtil.queryToString(_SearchService.getParsedForSearch(search));

export const SearchService = {
    loadArticles,
    calculateUrl,
    getPageFromSearch,
    DEFAULT_MAX_RESULTS,
    getSortFromSearch,
    getAscendingFromSearch,
    getProductFilterFromSearch,
    getShowAllFromSearch,
    getSearchId,
};
